import type { Products, SubscriptionsTypes } from '@strim/gateway-api';

import {
  HBOMaxBasisDisplayName,
  hboMaxDisplayName,
  strimSmartMedHBOMaxDisplayName,
  strimSmartMedHBOMaxFullDisplayName,
  strimSmartMedViaplayDisplayName,
  strimSmartMedViaplayFullDisplayName,
  viaplayDisplayName,
  ViaplayFilmSerierDisplayName,
} from '@rikstv/play-common/src/utils/displayNames/displayNames';
import { webAppLinks } from '@rikstv/shared-strim-paths';

import { ChannelOrService, CHANNELS, SERVICES } from './data/productChannelsAndServices';

/** Hardcoded flag — replaces LaunchDarkly 'strim-flov'. Set to true to enable flov product version. */
export const SHOW_FLOV_VERSION = true;

interface ProductEnrichmentBase {
  modalSellingPoints: string[];
  modalImage: string;
  cmsUrl: string;
  infoText?: string;
  content: {
    channels: ChannelOrService[];
    services: ChannelOrService[];
    optionalServices?: ChannelOrService[];
  };
}
interface ProductEnrichmentAddon {
  modalSellingPoints?: string[];
  modalImage?: never;
  cmsUrl?: never;
  infoText?: string;
  content: {
    channels: ChannelOrService[];
    services: ChannelOrService[];
    optionalServices?: ChannelOrService[];
  };
}
type ProductEnrichment = ProductEnrichmentBase | ProductEnrichmentAddon;

export const productNameToEnrichmentKeyRecord: Products.ProductNameToEnrichmentKeyMap = {
  StrimMindre: '844a2423-2985-4fd7-b460-68fa1e242161',
  StrimSmartMedViaplay: 'ea674889-b460-43da-b137-e83ef2195e67',
  StrimSmartMedHBOMax: 'c76433bb-0b6c-494d-a46b-660c6b80126e',
  StrimLitt: '814a2423-2985-4fd7-b460-68fa1e242161',
  StrimMye: '824a2423-2985-4fd7-b460-68fa1e242161',
  StrimMaks: '2615c95d-8439-4145-8fbd-a7c3df208dac',
  StrimMaksReklamefri: 'f6fa8b0d-188d-4030-af1c-1127da8de86a',
  StrimMest: '834a2423-2985-4fd7-b460-68fa1e242161',
  MaxReklamefri: 'c4c4c4ab-2448-4146-bdaa-1536e2c21876',
  MaxUltimate: 'b0bf9837-ec9a-4e2f-8f22-8b619188752b',
  MaxSport: '800306e4-63cb-4fee-b791-e6ff37e03506',
  ViaplayTotal: '7bf18231-0c8b-49ad-ad9e-762deb2f2d7b',
  ViaplayTotalPluss: '7bf18232-0c8b-49ad-ad9e-762deb2f2d7b',
  SportPremium: '3bfeb655-6186-4725-8844-2d03d6831c1f',
  TV2PlaySport: '87f50cc7-92b7-49fc-839d-7fc72e528251',
  TV2PlaySportPluss: '87f50cc8-92b7-49fc-839d-7fc72e528251',
  Sportskanaler: 'f87e7239-66e3-4263-8782-73630168cb22',
  TV2PlayUtenReklame: 'a5f6da6b-5e0c-47c8-b52f-6c553e824128',
  TV2PlayFavoritt: '3076a180-f11d-4cae-9ddb-7832fcd90c19',
  ViaplayFilmSerierUtenReklame: '17195812-9eda-4ba8-b64d-6fb6e8611ee3',
  MerUnderholdning: 'a369313b-3e7e-4622-90e2-c187ec775d13',
};
/** mainly here for exhaustiveness checks, so that we get type errors if more EK's are added */
export const productEnrichmentKeyToNameRecord = Object.fromEntries(
  Object.entries(productNameToEnrichmentKeyRecord).map(([key, value]) => [value, key])
) as Record<Products.EnrichmentKey, string>;

const MAX_ADDONS: Products.EnrichmentKey[] = [
  productNameToEnrichmentKeyRecord.MaxReklamefri,
  productNameToEnrichmentKeyRecord.MaxUltimate,
];
const TV2PLAY_ADDONS: Products.EnrichmentKey[] = [productNameToEnrichmentKeyRecord.TV2PlayUtenReklame];
const VIAPLAY_ADDONS: Products.EnrichmentKey[] = [productNameToEnrichmentKeyRecord.ViaplayFilmSerierUtenReklame];
const MER_UNDERHOLDNING_ADDONS: Products.EnrichmentKey[] = [productNameToEnrichmentKeyRecord.MerUnderholdning];
const GIRA_PA_MER_ADDONS: Products.EnrichmentKey[] = [
  productNameToEnrichmentKeyRecord.MerUnderholdning,
  productNameToEnrichmentKeyRecord.TV2PlayFavoritt,
];
const SPORT_ADDONS: Products.EnrichmentKey[] = [
  productNameToEnrichmentKeyRecord.MaxSport,
  productNameToEnrichmentKeyRecord.Sportskanaler,
  productNameToEnrichmentKeyRecord.TV2PlaySport,
  productNameToEnrichmentKeyRecord.ViaplayTotal,
  productNameToEnrichmentKeyRecord.SportPremium,
  productNameToEnrichmentKeyRecord.ViaplayTotalPluss,
  productNameToEnrichmentKeyRecord.TV2PlaySportPluss,
];

/** Custom order for sport addons in result of Pluss Products connected to their base */
export const SPORT_ADDON_DISPLAY_ORDER: Products.EnrichmentKey[] = [
  productNameToEnrichmentKeyRecord.MaxSport,
  productNameToEnrichmentKeyRecord.Sportskanaler,
  productNameToEnrichmentKeyRecord.TV2PlaySport,
  productNameToEnrichmentKeyRecord.TV2PlaySportPluss,
  productNameToEnrichmentKeyRecord.ViaplayTotal,
  productNameToEnrichmentKeyRecord.SportPremium,
  productNameToEnrichmentKeyRecord.ViaplayTotalPluss,
];

export const isMaxURAddon = (key: Products.EnrichmentKey) => MAX_ADDONS.includes(key);
export const isTV2PlayURAddon = (key: Products.EnrichmentKey) => TV2PLAY_ADDONS.includes(key);
export const isViaplayURAddon = (key: Products.EnrichmentKey) => VIAPLAY_ADDONS.includes(key);
export const isCommercialFreeAddon = (key: Products.EnrichmentKey) =>
  TV2PLAY_ADDONS.includes(key) || MAX_ADDONS.includes(key) || VIAPLAY_ADDONS.includes(key);

export const isMerUnderholdningAddon = (key: Products.EnrichmentKey) => MER_UNDERHOLDNING_ADDONS.includes(key);
export const isGiraPaMerAddon = (key: Products.EnrichmentKey) => GIRA_PA_MER_ADDONS.includes(key);
export const isSportAddon = (key: Products.EnrichmentKey) => SPORT_ADDONS.includes(key);

// Strim Smart
export const STRIM_SMART_ENRICHMENT_KEYS: Products.EnrichmentKey[] = [
  productNameToEnrichmentKeyRecord.StrimSmartMedViaplay,
  productNameToEnrichmentKeyRecord.StrimSmartMedHBOMax,
];
export const isStrimSmartProduct = (product: { enrichmentKey: Products.EnrichmentKey }) =>
  STRIM_SMART_ENRICHMENT_KEYS.includes(product.enrichmentKey);
export const isHBOMaxVariant = (enrichmentKey: Products.EnrichmentKey) =>
  enrichmentKey === productNameToEnrichmentKeyRecord.StrimSmartMedHBOMax;
export const isViaplayVariant = (enrichmentKey: Products.EnrichmentKey) =>
  enrichmentKey === productNameToEnrichmentKeyRecord.StrimSmartMedViaplay;

// Addons exclusive to each Strim Smart variant (upgrades only available with that variant)
const HBOMAX_VARIANT_EXCLUSIVE_ADDONS: Products.EnrichmentKey[] = [
  productNameToEnrichmentKeyRecord.MaxReklamefri,
  productNameToEnrichmentKeyRecord.MaxUltimate,
  productNameToEnrichmentKeyRecord.MaxSport,
];
const VIAPLAY_VARIANT_EXCLUSIVE_ADDONS: Products.EnrichmentKey[] = [
  productNameToEnrichmentKeyRecord.ViaplayFilmSerierUtenReklame,
];

/** Returns the addon enrichment keys that are exclusive to the OTHER variant (i.e. should be hidden/disabled) */
export const getOtherVariantExclusiveAddonKeys = (
  baseEnrichmentKey: Products.EnrichmentKey
): Products.EnrichmentKey[] => {
  if (isViaplayVariant(baseEnrichmentKey)) return HBOMAX_VARIANT_EXCLUSIVE_ADDONS;
  if (isHBOMaxVariant(baseEnrichmentKey)) return VIAPLAY_VARIANT_EXCLUSIVE_ADDONS;
  return [];
};

/** Check if an addon belongs to the other Strim Smart variant's exclusive upgrades */
export const isOtherVariantExclusiveAddon = (
  baseEnrichmentKey: Products.EnrichmentKey,
  addonEnrichmentKey: Products.EnrichmentKey
): boolean => {
  return getOtherVariantExclusiveAddonKeys(baseEnrichmentKey).includes(addonEnrichmentKey);
};
export interface VariantInfo {
  fullName: string;
  smartDisplayName: string;
  displayName: string;
  logoId: string;
  productDisplayName: string;
}

export const getVariantInfo = (enrichmentKey: Products.EnrichmentKey): VariantInfo => {
  if (isHBOMaxVariant(enrichmentKey)) {
    return {
      fullName: strimSmartMedHBOMaxFullDisplayName,
      smartDisplayName: strimSmartMedHBOMaxDisplayName,
      displayName: hboMaxDisplayName,
      logoId: 'hbomax_square.png',
      productDisplayName: HBOMaxBasisDisplayName,
    };
  }
  if (isViaplayVariant(enrichmentKey)) {
    return {
      fullName: strimSmartMedViaplayFullDisplayName,
      smartDisplayName: strimSmartMedViaplayDisplayName,
      displayName: viaplayDisplayName,
      logoId: 'Viaplay',
      productDisplayName: ViaplayFilmSerierDisplayName,
    };
  }
  return { fullName: '', smartDisplayName: '', displayName: '', logoId: '', productDisplayName: '' };
};
export const getOtherVariantEnrichmentKey = (enrichmentKey: Products.EnrichmentKey): Products.EnrichmentKey => {
  if (isHBOMaxVariant(enrichmentKey)) return productNameToEnrichmentKeyRecord.StrimSmartMedViaplay;
  else return productNameToEnrichmentKeyRecord.StrimSmartMedHBOMax;
};

// Strim Maks uten reklame: base product that should not be shown as a standalone base card
export const isStrimMaksReklamefri = (product: { enrichmentKey: Products.EnrichmentKey }) =>
  product.enrichmentKey === productNameToEnrichmentKeyRecord.StrimMaksReklamefri;

// Sport addon helpers, regarding Pluss products
export const isTV2PlaySportPluss = (key: Products.EnrichmentKey) =>
  key === productNameToEnrichmentKeyRecord.TV2PlaySportPluss;

export const isViaplayTotalPluss = (key: Products.EnrichmentKey) =>
  key === productNameToEnrichmentKeyRecord.ViaplayTotalPluss;

export const isTV2PlaySport = (key: Products.EnrichmentKey) => key === productNameToEnrichmentKeyRecord.TV2PlaySport;
export const isViaplayTotal = (key: Products.EnrichmentKey) => key === productNameToEnrichmentKeyRecord.ViaplayTotal;
export const isSportPremium = (key: Products.EnrichmentKey) => key === productNameToEnrichmentKeyRecord.SportPremium;

export const hasViaplayTotalOrSportPremium = (selectedEnrichmentKeys: Products.EnrichmentKey[]) =>
  selectedEnrichmentKeys.some(key => isViaplayTotal(key) || isSportPremium(key));
export const hasTV2PlaySportOrSportPremium = (selectedEnrichmentKeys: Products.EnrichmentKey[]) =>
  selectedEnrichmentKeys.some(key => isTV2PlaySport(key) || isSportPremium(key));

export const otherAddonsThatMustBeRemovedWhenAddonIsRemoved = (
  addonEnrichmentKey: Products.EnrichmentKey
): Products.EnrichmentKey[] => {
  if (isTV2PlaySport(addonEnrichmentKey)) return [productNameToEnrichmentKeyRecord.TV2PlaySportPluss];
  if (isViaplayTotal(addonEnrichmentKey)) return [productNameToEnrichmentKeyRecord.ViaplayTotalPluss];
  if (isSportPremium(addonEnrichmentKey))
    return [productNameToEnrichmentKeyRecord.TV2PlaySportPluss, productNameToEnrichmentKeyRecord.ViaplayTotalPluss];
  return [];
};

/**
 * HACK: Map from enrichment key to old package id's
 */
export const enrichmentKeyToPackageIdMap = new Map(
  Object.entries({
    [productNameToEnrichmentKeyRecord.StrimMindre]: '',
    [productNameToEnrichmentKeyRecord.StrimSmartMedViaplay]: '',
    [productNameToEnrichmentKeyRecord.StrimSmartMedHBOMax]: '',
    [productNameToEnrichmentKeyRecord.StrimLitt]: 'STRIM_MINI',
    [productNameToEnrichmentKeyRecord.StrimMye]: 'STRIM_FILM_OG_SERIER',
    [productNameToEnrichmentKeyRecord.StrimMest]: 'STRIM_FAMILIE',
    [productNameToEnrichmentKeyRecord.StrimMaks]: '',
    [productNameToEnrichmentKeyRecord.StrimMaksReklamefri]: '',
    [productNameToEnrichmentKeyRecord.MaxReklamefri]: '',
    [productNameToEnrichmentKeyRecord.MaxUltimate]: '',
    [productNameToEnrichmentKeyRecord.MaxSport]: '',
    [productNameToEnrichmentKeyRecord.MerUnderholdning]: '',
    [productNameToEnrichmentKeyRecord.TV2PlayFavoritt]: '',
    [productNameToEnrichmentKeyRecord.ViaplayTotal]: 'SPORT_2B',
    [productNameToEnrichmentKeyRecord.ViaplayTotalPluss]: '',
    [productNameToEnrichmentKeyRecord.SportPremium]: 'SPORT_VIAPLAY_TOTAL',
    [productNameToEnrichmentKeyRecord.TV2PlaySport]: 'SPORT',
    [productNameToEnrichmentKeyRecord.TV2PlaySportPluss]: '',
    [productNameToEnrichmentKeyRecord.Sportskanaler]: 'SPORTSKANALER',
    [productNameToEnrichmentKeyRecord.TV2PlayUtenReklame]: '',
    [productNameToEnrichmentKeyRecord.ViaplayFilmSerierUtenReklame]: '',
  } satisfies Record<Products.EnrichmentKey, AllowedIdValues>) as [Products.EnrichmentKey, AllowedIdValues][]
);
type AllowedIdValues = SubscriptionsTypes.Keys.BaseKeys | SubscriptionsTypes.Keys.AddonKeys | '';

// Reverse lookup from enrichment key to product name
export const packageIdToEnrichmentKeyMap = new Map(
  Array.from(enrichmentKeyToPackageIdMap)
    .filter(([, v]) => v !== '')
    .map(([k, v]) => [v, k])
) as ReadonlyMap<Exclude<AllowedIdValues, ''>, Products.EnrichmentKey>;

/**
 * Enrichments for use in sales
 */
export const productEnrichmentMap = new Map(
  Object.entries({
    // BASE PRODUCTS
    [productNameToEnrichmentKeyRecord.StrimMindre]: {
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/ef72ddc425411a0cb09ef2cfd1afb8fe1c3bfe32-1756x984.gif?w=750&q=75&fit=clip&auto=format',
      cmsUrl: '',
      modalSellingPoints: [
        '13 norske og internasjonale TV-kanaler',
        'Essensielle strømmetjenester',
        'Ingen bindingstid',
      ],
      content: {
        channels: CHANNELS.StrimChannels,
        services: [SERVICES.TV2Basic, SERVICES.MaxBasic],
      },
    },
    [productNameToEnrichmentKeyRecord.StrimSmartMedViaplay]: {
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/ef72ddc425411a0cb09ef2cfd1afb8fe1c3bfe32-1756x984.gif?w=750&q=75&fit=clip&auto=format',
      cmsUrl: webAppLinks.subscriptionStrimSmart,
      modalSellingPoints: ['13 TV-kanaler og 2 strømmetjenester', 'Ingen bindingstid'],
      content: {
        channels: CHANNELS.StrimChannels,
        services: [SERVICES.TV2Basic],
        optionalServices: [SERVICES.MaxBasic, SERVICES.ViaplayFilmSerierMedReklame],
      },
    },
    [productNameToEnrichmentKeyRecord.StrimSmartMedHBOMax]: {
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/ef72ddc425411a0cb09ef2cfd1afb8fe1c3bfe32-1756x984.gif?w=750&q=75&fit=clip&auto=format',
      cmsUrl: webAppLinks.subscriptionStrimSmart,
      modalSellingPoints: ['13 TV-kanaler og 2 strømmetjenester', 'Ingen bindingstid'],
      content: {
        channels: CHANNELS.StrimChannels,
        services: [SERVICES.TV2Basic],
        optionalServices: [SERVICES.MaxBasic, SERVICES.ViaplayFilmSerierMedReklame],
      },
    },
    [productNameToEnrichmentKeyRecord.StrimLitt]: {
      modalSellingPoints: ['9 norske og internasjonale TV-kanaler', 'Ingen bindingstid'],
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/403224ec47b36913f51e6cdcca85aa8e8d60ea01-1756x984.gif?w=2000&fit=max&auto=format',
      cmsUrl: webAppLinks.subscriptionStrimLitt,
      content: {
        channels: CHANNELS.LittChannels,
        services: [],
      },
    },

    [productNameToEnrichmentKeyRecord.StrimMye]: {
      modalSellingPoints: ['TV, film og serier fra 13 TV-kanaler og 5 strømmetjenester', 'Ingen bindingstid'],
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/52c4704bf8a70ad250e9f5f9eb0b7d1094178556-1756x984.gif?w=750&q=75&fit=clip&auto=format',
      cmsUrl: webAppLinks.subscriptionStrimMye,
      content: {
        channels: CHANNELS.MyeChannels,
        services: [
          SERVICES.TV2Basic,
          SERVICES.MaxBasic,
          SERVICES.ViaplayFilmSerierUtenReklame,
          SERVICES.SkyShowtime,
          SERVICES.NordicFilmPlus,
        ],
      },
    },
    [productNameToEnrichmentKeyRecord.StrimMaks]: {
      modalSellingPoints: ['TV, film og serier fra 13 TV-kanaler og 5 strømmetjenester', 'Ingen bindingstid'],
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/52c4704bf8a70ad250e9f5f9eb0b7d1094178556-1756x984.gif?w=750&q=75&fit=clip&auto=format',
      cmsUrl: webAppLinks.subscriptionStrimMaks,
      content: {
        channels: CHANNELS.MyeChannels,
        services: [
          SERVICES.TV2Basic,
          SERVICES.MaxBasic,
          SERVICES.ViaplayFilmSerierMedReklame,
          SERVICES.SkyShowtime,
          SERVICES.NordicFilmPlus,
        ],
      },
    },
    [productNameToEnrichmentKeyRecord.StrimMest]: {
      modalSellingPoints: [
        'TV, film og serier fra 30 TV-kanaler og 6 strømmetjenester',
        'Reklamefri TV 2 Play og alt av film og serier',
        'Ingen bindingstid',
      ],
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/066d73dc9ab60e2589ff89de1687dc87cbadb971-1756x984.gif?w=750&q=75&fit=clip&auto=format',
      cmsUrl: webAppLinks.subscriptionStrimMest,
      content: {
        channels: CHANNELS.MestChannels,
        services: [
          SERVICES.TV2PlayUtenReklame,
          SERVICES.MaxBasic,
          SERVICES.ViaplayFilmSerierUtenReklame,
          SERVICES.SkyShowtime,
          SERVICES.NordicFilmPlus,
          SERVICES.BBCNordicPlus,
        ],
      },
    },
    [productNameToEnrichmentKeyRecord.StrimMaksReklamefri]: {
      modalSellingPoints: [
        'TV, film og serier fra 13 TV-kanaler og 5 strømmetjenester',
        'Reklamefri TV 2 Play og alt av film og serier',
        'Ingen bindingstid',
      ],
      modalImage:
        'https://cdn.sanity.io/images/bj7ifs6s/strim-prod/52c4704bf8a70ad250e9f5f9eb0b7d1094178556-1756x984.gif?w=750&q=75&fit=clip&auto=format',
      cmsUrl: '',
      content: {
        channels: CHANNELS.MyeChannels,
        services: [
          SERVICES.TV2PlayUtenReklame,
          SERVICES.MaxStandard,
          SERVICES.ViaplayFilmSerierUtenReklame,
          SERVICES.SkyShowtime,
          SERVICES.NordicFilmPlus,
        ],
      },
    },
    // MAX ADDONS
    [productNameToEnrichmentKeyRecord.MaxReklamefri]: {
      content: {
        channels: [],
        services: [SERVICES.MaxStandard],
      },
    },
    [productNameToEnrichmentKeyRecord.MaxUltimate]: {
      content: {
        channels: [],
        services: [SERVICES.MaxPremium],
      },
    },
    // TV2 PLAY ADDONS
    [productNameToEnrichmentKeyRecord.TV2PlayFavoritt]: {
      modalSellingPoints: [
        'Disney+ inkludert',
        'Alt TV 2 Play har å by på innen håndball, ishockey, basketball, sykling, poker, sjakk og vintersport.',
      ],
      infoText: 'Om du har TV 2 Play med eller uten reklame, gjelder det også for Disney+.',
      content: {
        channels: [],
        services: [SERVICES.TV2DisneyPlus],
      },
    },
    [productNameToEnrichmentKeyRecord.TV2PlayUtenReklame]: {
      content: {
        channels: [],
        services: [SERVICES.TV2PlayUtenReklame],
      },
    },
    // VIAPLAY ADDON
    [productNameToEnrichmentKeyRecord.ViaplayFilmSerierUtenReklame]: {
      content: {
        channels: [],
        services: [SERVICES.ViaplayFilmSerierUtenReklame],
      },
    },
    // MER UNDERHOLDNING ADDON
    [productNameToEnrichmentKeyRecord.MerUnderholdning]: {
      content: {
        channels: CHANNELS.MerUnderholdningChannels,
        services: [SERVICES.BBCNordicPlus],
      },
    },
    // SPORT ADDONS
    [productNameToEnrichmentKeyRecord.ViaplayTotal]: {
      content: {
        channels: CHANNELS.SportChannels,
        services: [SERVICES.ViaplayTotal],
      },
    },
    [productNameToEnrichmentKeyRecord.ViaplayTotalPluss]: {
      content: {
        channels: [],
        services: [SERVICES.ViaplayTotalPluss],
      },
    },
    [productNameToEnrichmentKeyRecord.SportPremium]: {
      content: {
        channels: CHANNELS.SportChannels,
        services: [SERVICES.ViaplayTotal, SERVICES.TV2PlaySport],
      },
    },
    [productNameToEnrichmentKeyRecord.Sportskanaler]: {
      content: {
        channels: CHANNELS.SportChannels,
        services: [],
      },
    },
    [productNameToEnrichmentKeyRecord.TV2PlaySport]: {
      content: {
        channels: CHANNELS.SportChannels,
        services: [SERVICES.TV2PlaySport],
      },
    },
    [productNameToEnrichmentKeyRecord.TV2PlaySportPluss]: {
      content: {
        channels: [],
        services: [SERVICES.TV2PlaySportPluss],
      },
    },
    [productNameToEnrichmentKeyRecord.MaxSport]: {
      content: {
        channels: CHANNELS.MaxSportChannels,
        services: [SERVICES.MaxSport],
      },
    },
  } satisfies Record<Products.EnrichmentKey, ProductEnrichment>) as [Products.EnrichmentKey, ProductEnrichment][]
);
